// Author: Ahsan Saleem
/**
 * The sync outbox (plan §11.2, §11.3, §11.4).
 *
 * Client-generated commands are queued locally with an idempotency key and
 * retried in order. Because the server accepts each key exactly once, a
 * five-times-retried completion produces one terminal event (plan §14.2). We do
 * NOT depend on browser Background Sync — we retry on launch, foreground,
 * connectivity regain and explicit user action (plan §11.4).
 */
import { db, setSyncState, type OutboxItem } from './db.js';
import { api, ApiError } from './api.js';

const MAX_AUTO_ATTEMPTS = 5;
let flushing = false;

export function newIdempotencyKey(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function enqueue(item: Omit<OutboxItem, 'localId' | 'state' | 'attempts' | 'createdAt'>): Promise<void> {
  await db.outbox.add({ ...item, state: 'pending', attempts: 0, createdAt: new Date().toISOString() });
  emit();
  void flush();
}

export async function outboxItems(): Promise<OutboxItem[]> {
  return db.outbox.orderBy('localId').toArray();
}

export async function retryItem(localId: number): Promise<void> {
  await db.outbox.update(localId, { state: 'pending', attempts: 0, lastError: undefined });
  emit();
  void flush();
}

export async function flush(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const items = await db.outbox.orderBy('localId').toArray();
    for (const it of items) {
      if (it.state === 'failed' && it.attempts >= MAX_AUTO_ATTEMPTS) continue; // needs manual retry
      await db.outbox.update(it.localId!, { state: 'syncing' });
      emit();
      try {
        await api(it.path, { method: it.method, body: JSON.stringify(it.body) });
        await db.outbox.delete(it.localId!);
      } catch (e) {
        const err = e instanceof ApiError ? e : new ApiError(0, 'NETWORK', String(e));
        await db.outbox.update(it.localId!, {
          state: 'failed', attempts: it.attempts + 1, lastError: `${err.code}: ${err.message}`,
        });
      }
      emit();
    }
    await setSyncState({ lastSuccessAt: new Date().toISOString(), lastError: undefined });
  } finally {
    flushing = false;
    emit();
  }
}

function emit(): void {
  window.dispatchEvent(new Event('bloomos-sync'));
}

/** Wire the retry triggers. Called once at startup. */
export function installSyncTriggers(): void {
  window.addEventListener('online', () => { emit(); void flush(); });
  window.addEventListener('offline', emit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flush();
  });
  void flush(); // on launch
}
